import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { 
  Loader2, 
  Calendar as CalendarIcon, 
  Clock, 
  MapPin, 
  ChevronRight,
  X,
  Repeat,
  Building2,
  Users
} from "lucide-react";
import { getPublicEvents } from "../services/api";

const API_URL = import.meta.env.VITE_API_URL;

// Convert Firestore timestamps to JS Date
const toJsDate = (val) => {
  if (!val) return new Date();
  if (val instanceof Date) return val;
  if (val.toDate && typeof val.toDate === "function") return val.toDate();
  if (val.seconds) return new Date(val.seconds * 1000);
  if (typeof val === "string") return new Date(val);
  return new Date();
};

// Format date key (fallback when dateKey not available from backend)
const formatDateKey = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// Parse time to minutes for sorting
const parseTimeToMinutes = (timeStr) => {
  if (!timeStr) return 0;
  const parts = timeStr.trim().split(/\s+/);
  if (parts.length < 2) return 0;
  const [hours, minutes] = parts[0].split(':').map(Number);
  const ampm = parts[1].toLowerCase();
  let totalHours = hours;
  if (ampm === 'pm' && hours < 12) totalHours += 12;
  if (ampm === 'am' && hours === 12) totalHours = 0;
  return totalHours * 60 + (minutes || 0);
};

// Format date header
const formatDateHeader = (dateStr) => {
  const date = new Date(dateStr + 'T12:00:00');
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  const todayKey = formatDateKey(today);
  const tomorrowKey = formatDateKey(tomorrow);
  
  const options = { weekday: 'short', month: 'short', day: 'numeric' };
  
  if (dateStr === todayKey) return `Today (${date.toLocaleDateString('en-US', options)})`;
  if (dateStr === tomorrowKey) return `Tomorrow (${date.toLocaleDateString('en-US', options)})`;
  
  return date.toLocaleDateString('en-US', options);
};

// Format recurrence rule
const formatRecurrenceRule = (recurrence, event = null) => {
  if (!recurrence || recurrence.type === "never") return null;
  
  const { type, frequency, interval, byDay, bySetPos, byMonthDay } = recurrence;
  
  const dayMap = {
    SU: 'Sunday', MO: 'Monday', TU: 'Tuesday', WE: 'Wednesday',
    TH: 'Thursday', FR: 'Friday', SA: 'Saturday'
  };
  
  const ordinalMap = {
    1: 'First', 2: 'Second', 3: 'Third', 4: 'Fourth', 5: 'Fifth',
    '-1': 'Last', '-2': 'Second to last'
  };
  
  const getPrayerSuffix = () => {
    if (event?.timeType === 'prayer' && event?.prayerTimeConfig?.startPrayer) {
      return ` after ${event.prayerTimeConfig.startPrayer}`;
    }
    return '';
  };
  
  let baseText = "";
  
  if (type === "weekly" || frequency === "WEEKLY") {
    const days = byDay && byDay.length > 0 ? byDay.map(d => dayMap[d] || d).join(", ") : "";
    if (interval === 1) {
      baseText = days ? `Weekly on ${days}` : "Weekly";
    } else if (interval === 2) {
      baseText = days ? `Biweekly on ${days}` : "Biweekly";
    } else {
      baseText = days ? `Every ${interval} weeks on ${days}` : `Every ${interval} weeks`;
    }
  } else if (type === "daily" || frequency === "DAILY") {
    baseText = interval === 1 ? "Daily" : `Every ${interval} days`;
  } else if (type === "monthly" || frequency === "MONTHLY") {
    if (bySetPos && bySetPos.length > 0 && byDay && byDay.length > 0) {
      const pos = bySetPos[0];
      const day = dayMap[byDay[0]] || byDay[0];
      const ordinal = ordinalMap[pos] || `${pos}th`;
      baseText = interval === 1 ? `Monthly on the ${ordinal} ${day}` : `Every ${interval} months`;
    } else if (byMonthDay && byMonthDay.length > 0) {
      baseText = interval === 1 ? `Monthly on day ${byMonthDay[0]}` : `Every ${interval} months`;
    } else {
      baseText = interval === 1 ? "Monthly" : `Every ${interval} months`;
    }
  } else if (type === "yearly" || frequency === "YEARLY") {
    baseText = interval === 1 ? "Yearly" : `Every ${interval} years`;
  } else {
    baseText = "Recurring";
  }
  
  baseText += getPrayerSuffix();
  return baseText;
};

// Group events by date
const groupEventsByDate = (events) => {
  const grouped = {};
  
  events.forEach(event => {
    // Use dateKey from backend if available (timezone-correct)
    // Fall back to recurrenceInstanceId date, then event.date
    let dateKey;
    if (event.dateKey) {
      // Backend provides timezone-correct date key
      dateKey = event.dateKey;
    } else if (event.recurrenceInstanceId) {
      // recurrenceInstanceId format is "eventId_YYYY-MM-DD"
      const parts = event.recurrenceInstanceId.split('_');
      dateKey = parts.length > 1 ? parts[parts.length - 1] : formatDateKey(toJsDate(event.date));
    } else {
      dateKey = formatDateKey(toJsDate(event.date));
    }
    if (!grouped[dateKey]) {
      grouped[dateKey] = [];
    }
    grouped[dateKey].push(event);
  });
  
  // Sort events within each day by time
  Object.keys(grouped).forEach(dateKey => {
    grouped[dateKey].sort((a, b) => {
      const aStart = parseTimeToMinutes(a.time);
      const bStart = parseTimeToMinutes(b.time);
      return aStart - bStart;
    });
  });
  
  return Object.keys(grouped).sort().map(date => ({
    title: date,
    data: grouped[date]
  }));
};

export default function EventAgendaWidget() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const theme = searchParams.get("theme") || "light";
  const color = searchParams.get("color") || "bf9a2f";
  const widthParam = searchParams.get("width") || "max";
  const heightParam = searchParams.get("height") || "max";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [masjidData, setMasjidData] = useState(null);
  const [masjidId, setMasjidId] = useState(null);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const isDark = theme === "dark";
  const accentColor = `#${color}`;
  const bgColor = isDark ? "#1a1a1a" : "#ffffff";
  const textColor = isDark ? "#ffffff" : "#0f172a";
  const mutedColor = isDark ? "#9ca3af" : "#64748b";
  const borderColor = isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)";
  const cardBg = isDark ? "#222" : "#fff";
  const sectionBg = isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)";
  const outerBorder = isDark ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid rgba(0, 0, 0, 0.1)";

  // Container style with border and rounded corners
  const containerStyle = {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    backgroundColor: bgColor,
    width: widthParam === "max" ? "100vw" : `${widthParam}px`,
    height: heightParam === "max" ? "100vh" : `${heightParam}px`,
    minHeight: "400px",
    borderRadius: widthParam === "max" && heightParam === "max" ? "0" : "12px",
    border: widthParam === "max" && heightParam === "max" ? "none" : outerBorder,
    overflow: "hidden",
    boxSizing: "border-box",
  };

  // Load masjid info
  useEffect(() => {
    if (!token) return;

    const loadMasjidInfo = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/v1/widget-data/${token}`);
        if (!response.ok) throw new Error("Failed to load widget data");

        const data = await response.json();
        if (!data.ok) throw new Error(data.error || "Failed to load widget data");

        setMasjidData(data.masjid);
        setMasjidId(data.masjid.id || data.masjid.masjidId || data.masjid.key);
      } catch (err) {
        setError(err.message || "Failed to load masjid data");
      } finally {
        setLoading(false);
      }
    };

    loadMasjidInfo();
  }, [token]);

  // Fetch events via API
  useEffect(() => {
    if (!masjidId) return;

    const fetchEvents = async () => {
      try {
        setLoading(true);
        
        // Use the events API with masjid filter
        const result = await getPublicEvents({ 
          masjidId, 
          days: 90,  // 3 months
          limit: 200 
        });
        
        const eventsList = result.events || [];
        eventsList.sort((a, b) => {
          const dateA = toJsDate(a.date);
          const dateB = toJsDate(b.date);
          return dateA - dateB;
        });

        setEvents(eventsList);
      } catch (err) {
        console.error("Error fetching events:", err);
        setError(err.message || "Failed to load events");
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [masjidId]);

  const sections = groupEventsByDate(events);

  // Event Detail Modal
  const EventDetailModal = ({ event, onClose }) => {
    if (!event) return null;

    const eventDate = toJsDate(event.date);
    const formattedDate = eventDate.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    return (
      <div 
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "16px",
          zIndex: 1000,
        }}
        onClick={onClose}
      >
        <div 
          style={{
            backgroundColor: cardBg,
            borderRadius: "16px",
            width: "100%",
            maxWidth: "400px",
            maxHeight: "80vh",
            overflow: "hidden",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div style={{ padding: "16px 20px", borderBottom: `1px solid ${borderColor}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px" }}>
              <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "700", color: textColor, lineHeight: "1.3" }}>
                {event.title}
              </h3>
              <button
                onClick={onClose}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "4px",
                  color: mutedColor,
                  flexShrink: 0,
                }}
              >
                <X size={20} />
              </button>
            </div>
            {event.masjid && (
              <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "8px" }}>
                <Building2 size={14} color={accentColor} />
                <span style={{ fontSize: "13px", fontWeight: "600", color: accentColor }}>
                  {event.masjid}
                </span>
              </div>
            )}
          </div>

          {/* Modal Content */}
          <div style={{ padding: "16px 20px", overflowY: "auto", maxHeight: "calc(80vh - 120px)" }}>
            {/* When */}
            <div style={{ marginBottom: "16px" }}>
              <div style={{ fontSize: "11px", fontWeight: "700", color: mutedColor, letterSpacing: "0.5px", marginBottom: "8px" }}>
                WHEN
              </div>
              <div style={{ backgroundColor: sectionBg, borderRadius: "10px", padding: "12px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <CalendarIcon size={18} color={accentColor} />
                  <div>
                    <div style={{ fontSize: "14px", fontWeight: "600", color: textColor }}>{formattedDate}</div>
                    {event.time && (
                      <div style={{ fontSize: "13px", color: mutedColor, marginTop: "2px" }}>
                        {event.time}{event.endTime && ` - ${event.endTime}`}
                      </div>
                    )}
                  </div>
                </div>
                {event.recurrence && event.recurrence.type !== "never" && (
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "10px", paddingTop: "10px", borderTop: `1px solid ${borderColor}` }}>
                    <Repeat size={18} color={accentColor} />
                    <span style={{ fontSize: "13px", color: textColor }}>{formatRecurrenceRule(event.recurrence, event)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Where */}
            {(event.location || event.sublocation) && (
              <div style={{ marginBottom: "16px" }}>
                <div style={{ fontSize: "11px", fontWeight: "700", color: mutedColor, letterSpacing: "0.5px", marginBottom: "8px" }}>
                  WHERE
                </div>
                <a 
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "none", display: "block" }}
                >
                  <div style={{ backgroundColor: sectionBg, borderRadius: "10px", padding: "12px", cursor: "pointer", transition: "all 0.2s" }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = sectionBg}
                  >
                    <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                      <MapPin size={18} color={accentColor} style={{ flexShrink: 0, marginTop: "2px" }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: "14px", fontWeight: "600", color: accentColor }}>{event.location}</div>
                        {event.sublocation && (
                          <div style={{ fontSize: "13px", color: mutedColor, marginTop: "2px" }}>{event.sublocation}</div>
                        )}
                      </div>
                      <ChevronRight size={16} color={mutedColor} style={{ flexShrink: 0, marginTop: "2px" }} />
                    </div>
                  </div>
                </a>
              </div>
            )}

            {/* Description */}
            {event.description && (
              <div>
                <div style={{ fontSize: "11px", fontWeight: "700", color: mutedColor, letterSpacing: "0.5px", marginBottom: "8px" }}>
                  DESCRIPTION
                </div>
                <p style={{ margin: 0, fontSize: "14px", lineHeight: "1.6", color: isDark ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.7)", whiteSpace: "pre-wrap" }}>
                  {event.description}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (!token) {
    return (
      <div style={{ ...containerStyle, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>🕌</div>
          <p style={{ color: "#ef4444", fontSize: "18px", fontWeight: "600", margin: 0 }}>Missing Widget Token</p>
          <p style={{ color: mutedColor, fontSize: "14px", marginTop: "8px" }}>Please configure this widget from your masjid dashboard</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ ...containerStyle, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "20px" }}>
        <Loader2 style={{ width: "48px", height: "48px", color: accentColor, animation: "spin 1s linear infinite" }} />
        <p style={{ marginTop: "16px", color: mutedColor, fontSize: "14px" }}>Loading events...</p>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ ...containerStyle, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "20px", textAlign: "center" }}>
        <div style={{ width: "56px", height: "56px", borderRadius: "50%", backgroundColor: "rgba(239, 68, 68, 0.1)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "16px" }}>
          <span style={{ fontSize: "28px" }}>⚠️</span>
        </div>
        <p style={{ color: "#ef4444", fontSize: "16px", fontWeight: "600", margin: 0 }}>{error}</p>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div style={{ ...containerStyle, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 20px", textAlign: "center" }}>
        <CalendarIcon style={{ width: "64px", height: "64px", color: mutedColor, opacity: 0.3, marginBottom: "16px" }} />
        <h3 style={{ fontSize: "20px", fontWeight: "700", color: textColor, margin: "0 0 8px 0" }}>No Upcoming Events</h3>
        <p style={{ color: mutedColor, fontSize: "14px", margin: 0 }}>Check back later for new events from {masjidData?.name}</p>
      </div>
    );
  }

  return (
    <div style={{ ...containerStyle, display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div style={{ textAlign: "center", padding: "16px", borderBottom: `1px solid ${borderColor}`, background: isDark ? "linear-gradient(to bottom, rgba(255, 255, 255, 0.03), transparent)" : "linear-gradient(to bottom, rgba(0, 0, 0, 0.02), transparent)" }}>
        <h2 style={{ margin: "0 0 4px 0", fontSize: "20px", fontWeight: "700", color: accentColor, letterSpacing: "0.5px" }}>
          UPCOMING EVENTS
        </h2>
        {masjidData?.name && (
          <p style={{ margin: "0", fontSize: "13px", color: mutedColor, fontWeight: "500" }}>
            {masjidData.name}
          </p>
        )}
      </div>

      {/* Event List */}
      <div style={{ flex: 1, overflowY: "auto" }}>
        {sections.map((section) => (
          <div key={section.title}>
            {/* Date Header */}
            <div style={{
              padding: "12px 16px",
              backgroundColor: isDark ? "#252525" : "#f5f5f5",
              borderBottom: `1px solid ${borderColor}`,
              position: "sticky",
              top: 0,
              zIndex: 10,
            }}>
              <span style={{ fontSize: "15px", fontWeight: "700", color: textColor }}>
                {formatDateHeader(section.title)}
              </span>
            </div>

            {/* Events for this date */}
            {section.data.map((event) => (
              <div
                key={event.recurrenceInstanceId || event.id}
                onClick={() => setSelectedEvent(event)}
                style={{
                  padding: "14px 16px",
                  borderBottom: `1px solid ${borderColor}`,
                  cursor: "pointer",
                  transition: "background-color 0.2s",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)"}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
              >
                {/* Left border accent */}
                <div style={{ width: "3px", height: "40px", backgroundColor: accentColor, borderRadius: "2px", flexShrink: 0 }} />
                
                {/* Event content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: "15px", fontWeight: "600", color: textColor, marginBottom: "4px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {event.title}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                      <Clock size={12} color={mutedColor} />
                      <span style={{ fontSize: "12px", color: mutedColor }}>
                        {event.time}{event.endTime && ` - ${event.endTime}`}
                      </span>
                    </div>
                    {event.masjid && (
                      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <Building2 size={12} color={accentColor} />
                        <span style={{ fontSize: "12px", color: accentColor, fontWeight: "500" }}>{event.masjid}</span>
                      </div>
                    )}
                  </div>
                  {event.recurrence && event.recurrence.type !== "never" && (
                    <div style={{ display: "flex", alignItems: "center", gap: "4px", marginTop: "4px" }}>
                      <Repeat size={11} color={accentColor} />
                      <span style={{ fontSize: "11px", color: accentColor, fontWeight: "500" }}>{formatRecurrenceRule(event.recurrence, event)}</span>
                    </div>
                  )}
                </div>

                {/* Chevron */}
                <ChevronRight size={18} color={isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)"} style={{ flexShrink: 0 }} />
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Footer */}
      <div style={{ padding: "10px", borderTop: `1px solid ${borderColor}`, textAlign: "center" }}>
        <a href="https://muslimplus.org" target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "11px", color: mutedColor, textDecoration: "none" }}>
          <span>Powered by</span>
          <span style={{ fontWeight: "700", color: accentColor }}>Muslim+</span>
        </a>
      </div>

      {/* Event Detail Modal */}
      {selectedEvent && (
        <EventDetailModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />
      )}
    </div>
  );
}
